import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore, hospitals } from '@/stores/authStore'
import { useMedicalRecordStore } from '@/stores/medicalRecordStore'

export default function MedicalRecordList() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterHospital, setFilterHospital] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')
  const { user } = useAuthStore()
  const { records } = useMedicalRecordStore()

  const isAdminOrOffice = user?.role === 'admin' || user?.role === 'office'
  const isPatient = user?.role === 'patient'
  const userHospitalId = user?.hospitalId
  const userPatientRecordId = user?.patientRecordId

  // フィルタリング済み診療記録
  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      // 患者の場合は自分のカルテのみ
      if (isPatient && userPatientRecordId) {
        if (record.patientRecordId !== userPatientRecordId) {
          return false
        }
      }
      // 医師の場合は自院データのみ
      else if (!isAdminOrOffice && userHospitalId) {
        if (record.hospitalId !== userHospitalId) {
          return false
        }
      }

      // 病院フィルター
      if (filterHospital !== 'all' && record.hospitalId !== filterHospital) {
        return false
      }

      // 記録タイプフィルター
      if (filterType !== 'all' && record.recordType !== filterType) {
        return false
      }

      // 検索クエリ
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const content = `${record.subjectiveNote} ${record.objectiveNote} ${record.assessmentNote} ${record.planNote}`
        return (
          record.patientName.toLowerCase().includes(query) ||
          record.researchId.toLowerCase().includes(query) ||
          record.doctorName.toLowerCase().includes(query) ||
          content.toLowerCase().includes(query)
        )
      }

      return true
    }).sort((a, b) => new Date(b.recordDate).getTime() - new Date(a.recordDate).getTime())
  }, [records, searchQuery, filterHospital, filterType, isAdminOrOffice, isPatient, userHospitalId, userPatientRecordId])

  const getContentPreview = (record: typeof records[0]) => {
    const parts = []
    if (record.subjectiveNote) parts.push(`S)${record.subjectiveNote.substring(0, 30)}`)
    if (record.objectiveNote) parts.push(`O)${record.objectiveNote.substring(0, 30)}`)
    return parts.join(' ') || '記録なし'
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <h1 className="aip-main__header-title">診療記録</h1>
          {!isAdminOrOffice && !isPatient && user?.hospitalName && (
            <span className="aip-main__header-subtitle">
              <span className="material-symbols-outlined">local_hospital</span>
              {user.hospitalName}
            </span>
          )}
          {isPatient && (
            <span className="aip-main__header-subtitle aip-main__header-subtitle--patient">
              <span className="material-symbols-outlined">person</span>
              {user?.displayName}さんの記録
            </span>
          )}
          <div className="aip-main__header-buttons">
            {!isPatient && (
              <Link to="/medical-records/create" className="aip-button aip-button--solid-fill">
                <span className="material-symbols-outlined">add</span>
                新規作成
              </Link>
            )}
            {isPatient && (
              <Link to="/patient/daily-record" className="aip-button aip-button--solid-fill">
                <span className="material-symbols-outlined">edit_note</span>
                今日の記録を入力
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="aip-main__content">
        <div className="list-container">
          <div className="aip-table__header">
            <div className="aip-table__header-area">
              <div className="aip-search-bar">
                <span className="material-symbols-outlined aip-search-bar__icon">search</span>
                <input
                  className="aip-search-bar__input"
                  type="text"
                  placeholder="患者名、研究会ID、医師名で検索"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {isAdminOrOffice && (
                <select
                  className="aip-select"
                  value={filterHospital}
                  onChange={(e) => setFilterHospital(e.target.value)}
                >
                  <option value="all">すべての病院</option>
                  {hospitals.map((hospital) => (
                    <option key={hospital.id} value={hospital.id}>
                      {hospital.name}
                    </option>
                  ))}
                </select>
              )}

              <select
                className="aip-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">すべての記録</option>
                <option value="doctor">医師記録</option>
                <option value="patient">患者回答</option>
              </select>
            </div>
          </div>

          <div className="aip-table-wrapper">
            <table className="aip-table">
              <thead>
                <tr>
                  <th>記録日</th>
                  <th>種別</th>
                  <th>研究会ID</th>
                  <th>氏名</th>
                  {!isPatient && <th>医療機関</th>}
                  <th>担当医師</th>
                  <th>診療内容</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => (
                  <tr key={record.id}>
                    <td>
                      <Link to={`/medical-records/${record.id}`} className="aip-table__link">
                        {record.recordDate}
                      </Link>
                    </td>
                    <td>
                      <span className={`record-type-badge record-type-badge--${record.recordType}`}>
                        {record.recordType === 'doctor' ? '医師' : '患者'}
                      </span>
                    </td>
                    <td>
                      <Link to={`/patients/${record.patientRecordId}`} className="aip-table__link">
                        {record.researchId}
                      </Link>
                    </td>
                    <td>{record.patientName}</td>
                    {!isPatient && (
                      <td>
                        <Link to={`/master/hospitals/${record.hospitalId}`} className="hospital-link">
                          {record.hospitalName}
                        </Link>
                      </td>
                    )}
                    <td>
                      <div className="doctor-cell">
                        <span className="doctor-avatar">{record.doctorName.charAt(0)}</span>
                        {record.doctorName}
                      </div>
                    </td>
                    <td className="content-cell">{getContentPreview(record)}...</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredRecords.length === 0 && (
            <div className="aip-table__empty">
              <span className="material-symbols-outlined">search_off</span>
              <p>該当する診療記録がありません</p>
            </div>
          )}

          <div className="aip-table__pagination">
            <span className="aip-table__pagination-info">
              1 - {filteredRecords.length} （{filteredRecords.length}件中）
            </span>
          </div>
        </div>
      </div>

      <style>{`
        .list-container {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .aip-main__header-subtitle {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: 0.875rem;
          color: var(--color-blue600);
          background-color: var(--color-blue50);
          padding: var(--spacing-xs) var(--spacing-sm);
          border-radius: var(--border-radius-md);
          margin-left: var(--spacing-md);
        }

        .aip-main__header-subtitle--patient {
          background-color: var(--color-success-light);
          color: var(--color-success);
        }

        .aip-main__header-subtitle .material-symbols-outlined {
          font-size: 18px;
        }

        .aip-table__header {
          padding: var(--spacing-md);
          border-bottom: 1px solid var(--border-color);
        }

        .aip-table__header-area {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          flex-wrap: wrap;
        }

        .aip-select {
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          background-color: var(--background-primary);
          max-width: 200px;
        }

        .aip-table-wrapper {
          overflow-x: auto;
        }

        .aip-table {
          width: 100%;
          border-collapse: collapse;
        }

        .aip-table th, .aip-table td {
          padding: var(--spacing-sm) var(--spacing-md);
          text-align: left;
          border-bottom: 1px solid var(--border-color);
          font-size: 0.8125rem;
        }

        .aip-table th {
          background-color: var(--color-solid-gray25);
          font-weight: 500;
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .aip-table tbody tr:hover {
          background-color: var(--color-solid-gray25);
        }

        .aip-table__link {
          color: var(--color-blue600);
          font-weight: 500;
        }

        .record-type-badge {
          display: inline-block;
          padding: 2px 8px;
          font-size: 0.6875rem;
          font-weight: 500;
          border-radius: var(--border-radius-sm);
        }

        .record-type-badge--doctor {
          background-color: var(--color-blue50);
          color: var(--color-blue600);
        }

        .record-type-badge--patient {
          background-color: var(--color-success-light);
          color: var(--color-success);
        }

        .hospital-link {
          color: var(--text-primary);
          font-size: 0.75rem;
        }

        .hospital-link:hover {
          color: var(--color-blue600);
          text-decoration: underline;
        }

        .doctor-cell {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
        }

        .doctor-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background-color: var(--color-blue500);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.6875rem;
          font-weight: 600;
        }

        .content-cell {
          max-width: 300px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .aip-table__empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--spacing-xl);
          color: var(--text-secondary);
        }

        .aip-table__empty .material-symbols-outlined {
          font-size: 48px;
          margin-bottom: var(--spacing-sm);
          opacity: 0.5;
        }

        .aip-table__pagination {
          padding: var(--spacing-md);
          border-top: 1px solid var(--border-color);
        }
      `}</style>
    </>
  )
}
